// The prompt catalog — the copy-paste instructions people hand to their agent.
//
// These ARE the product's interface, so they're treated as product surface, not
// marketing copy. Two rules:
//
//   1. Point the agent at /llms.txt rather than enumerating commands. The docs
//      are versioned and always current; a prompt baked with commands rots.
//   2. Tell the agent to be HONEST. The migration and evaluation prompts
//      explicitly instruct it to report what does NOT fit before touching
//      anything. A platform that only ships prompts which say "yes" is one that
//      surprises people in production — and we publish the limits anyway, so
//      the agent may as well read them out loud.

export interface AgentPrompt {
	id: string;
	/** Tab label — short, verb-first. */
	label: string;
	/** One line under the box: who this is for. */
	hint: string;
	build: (origin: string) => string;
}

const INTRO = (origin: string) =>
	`s3p (${origin}) — S3 is the database, Cognito is auth, IAM is the security. ` +
	"No servers to run, no AWS account needed; backend code (when you need it) is a serverless function.";

/** Account creation, spelled out once and reused: the ONE human-in-the-loop step. */
const SIGNUP_STEPS =
	"Create my account yourself with the CLI (npm i -g @s3p/cli):\n" +
	"  s3p signup --email <MY_EMAIL>     # I'll paste you the code from my inbox\n" +
	"  s3p confirm --code <CODE>";

export const AGENT_PROMPTS: AgentPrompt[] = [
	{
		id: "new",
		label: "Build a new app",
		hint: "You have an idea. Your agent takes it from nothing to a live URL.",
		build: (o) =>
			[
				`Build and deploy a web app for me on ${INTRO(o)}`,
				"",
				`Start by reading ${o}/llms.txt — it links everything you need to know.`,
				"",
				SIGNUP_STEPS,
				"",
				"Then create the project, write the app, and deploy it.",
				"",
				"First, ask me what the app should do.",
			].join("\n"),
	},
	{
		id: "migrate",
		label: "Deploy an app I have",
		hint: "Point your agent at an existing codebase. It audits it before moving anything.",
		build: (o) =>
			[
				`I have an existing web app. I want to move it to ${INTRO(o)}`,
				"",
				`First read ${o}/llms.txt — especially ${o}/docs/database-capabilities.md,`,
				"which states plainly what S3 can and cannot do as a database.",
				"",
				"Then AUDIT my app before you change anything, and check:",
				"  - Frontend: is it a static SPA? (any framework is fine; server-rendered is not)",
				"  - Data: what does it store, and does it rely on JOINs, multi-record",
				"    transactions, or big scans? Those do NOT port — say so if they're there.",
				"  - Backend: is there an always-on server, WebSockets, or background workers?",
				"    s3p only has serverless functions (≤15 min, no long-lived state).",
				"  - Auth, file uploads, secrets, outbound email, scheduled jobs.",
				"",
				"Then tell me STRAIGHT, before migrating: what ports cleanly, what needs",
				"rework, and what doesn't fit at all. Don't start the migration until I say go.",
				"",
				"If I say go:",
				SIGNUP_STEPS,
				"",
				"…then create the project, port the data layer to the s3p SDK, move any",
				"server-only code into serverless functions, and deploy it.",
			].join("\n"),
	},
	{
		id: "evaluate",
		label: "Is it right for me?",
		hint: "No signup. Your agent reads the docs — including the limits — and gives you a verdict.",
		build: (o) =>
			[
				`I'm evaluating ${INTRO(o)}`,
				"",
				`Read ${o}/llms.txt, and specifically the honest limits in`,
				`${o}/docs/database-capabilities.md.`,
				"",
				"Then tell me whether it actually fits what I'm building. Don't sell it to me.",
				"If my use case needs SQL joins, transactions across records, sub-second",
				"realtime, or an always-on server, say so plainly and tell me what I should",
				"use instead. If it does fit, tell me exactly what I'd get and what I'd give up.",
				"",
				"Ask me what I'm building, then give me your verdict.",
			].join("\n"),
	},
];

/** Prompts for people who ALREADY have a project — shown on /app, not the
 *  landing. Same rules: point at the docs, let the agent do the work. */
export const PROJECT_PROMPTS: AgentPrompt[] = [
	{
		id: "feature",
		label: "Ship a change",
		hint: "Add a feature to an existing app and deploy it.",
		build: (o) =>
			[
				`Work on my s3p app (${o}). The project is in this directory (s3p.project.json).`,
				"",
				`Read ${o}/llms.txt if you don't already know the SDK.`,
				"",
				"I'll describe the change I want. Implement it with the s3p SDK, then:",
				"  s3p build && s3p deploy",
				"",
				"Ask me what to change.",
			].join("\n"),
	},
	{
		id: "function",
		label: "Add a serverless function",
		hint: "For secrets, webhooks, scheduled jobs — anything the browser can't do safely.",
		build: (o) =>
			[
				`Add a serverless function to my s3p app (${o}).`,
				"",
				`Read ${o}/docs/functions.md first.`,
				"",
				"Rule of thumb you should apply: PREFER THE BROWSER. Only use a function if",
				"it needs a secret the client must never see, a public webhook URL, a",
				"schedule, or something AWS won't let a browser do safely. If what I'm asking",
				"for doesn't need a function, tell me and do it client-side instead.",
				"",
				"Write it in functions/, wire it up with app.fn(...), then s3p deploy.",
				"",
				"Ask me what the function should do.",
			].join("\n"),
	},
	{
		id: "users",
		label: "Add users & roles",
		hint: "Sign-in, permissions, and who can read or write what.",
		build: (o) =>
			[
				`Set up users and roles for my s3p app (${o}).`,
				"",
				`Read ${o}/docs/definitions.md and the roles/access parts of ${o}/llms.txt.`,
				"",
				"Remember: authorization is enforced by AWS IAM, not by my app code. Express",
				"who-can-read-what in s3p.permissions.json, then sync it — don't",
				"write permission checks in the frontend and call it security.",
				"",
				"Ask me who the users are and what each role should be able to do.",
			].join("\n"),
	},
	{
		id: "domain",
		label: "Use my own domain",
		hint: "Put the app on your domain, with email from it too.",
		build: (o) =>
			[
				`Attach my custom domain to my s3p app (${o}).`,
				"",
				`Read the domains section of ${o}/llms.txt.`,
				"",
				"Use app.domains.add(...) to start it, give me the DNS records to publish,",
				"then verify once they've propagated.",
				"",
				"Ask me for the domain.",
			].join("\n"),
	},
];
